import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LogOut, LayoutDashboard, Settings, Crown, Menu, X, UserCog } from 'lucide-react';

// Color Mapping for Avatars
const AVATAR_COLORS = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    pink: 'bg-pink-500',
    red: 'bg-red-500',
};

export const Navbar = () => {
    const { user, isAuthenticated, isPro, isAdmin, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path) => location.pathname === path;

    // Safe background color fallback
    const userBgColor = (user?.avatar_color && AVATAR_COLORS[user.avatar_color])
        ? AVATAR_COLORS[user.avatar_color]
        : 'bg-primary/20';

    const isAnnual = user?.plan_interval === 'year';

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border transition-all duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* HEIGHT CHANGE: 
                    Mobile: h-16 (64px)
                    Desktop: md:h-24 (96px) 
                */}
                <div className="flex items-center justify-between h-16 md:h-24 transition-all duration-300">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        {/* LOGO SIZE CHANGE:
                            Mobile: h-12 
                            Desktop: md:h-16 
                        */}
                        <img
                            src="/subhack_logo.svg"
                            alt="SubHack Logo"
                            className="h-12 md:h-16 w-auto object-contain transition-all duration-300"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {/* FONT SIZE CHANGE:
                           Mobile: text-sm (default)
                           Desktop: md:text-lg 
                        */}
                        <Link
                            to="/dashboard"
                            className={`font-heading text-sm md:text-lg uppercase tracking-wider transition-colors ${isActive('/dashboard') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                                }`}
                            data-testid="nav-dashboard"
                        >
                            My Dashboard
                        </Link>
                        <Link
                            to="/gallery"
                            className={`font-heading text-sm md:text-lg uppercase tracking-wider transition-colors ${isActive('/gallery') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                                }`}
                            data-testid="nav-gallery"
                        >
                            Gallery
                        </Link>
                        <Link
                            to="/leaderboard"
                            className={`font-heading text-sm md:text-lg uppercase tracking-wider transition-colors ${isActive('/leaderboard') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                                }`}
                            data-testid="nav-leaderboard"
                        >
                            Leaderboard
                        </Link>
                        <Link
                            to="/pricing"
                            className={`font-heading text-sm md:text-lg uppercase tracking-wider transition-colors ${isActive('/pricing') ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                                }`}
                            data-testid="nav-pricing"
                        >
                            Pricing
                        </Link>

                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="flex items-center gap-2 relative pl-2 pr-2"
                                        data-testid="user-menu-trigger"
                                    >
                                        {/* AVATAR SIZE CHANGE:
                                            Mobile: w-9 h-9 
                                            Desktop: md:w-12 md:h-12
                                        */}
                                        <div className={`relative w-9 h-9 md:w-12 md:h-12 rounded-full flex items-center justify-center p-[2px] transition-all duration-300
                                            ${isAnnual
                                                ? 'bg-gradient-to-tr from-[#FFD700] via-[#FFA500] to-[#FFD700] shadow-[0_0_10px_rgba(255,215,0,0.5)]' // GOLD GLOW
                                                : (isPro ? 'bg-primary/50' : 'bg-transparent') // Standard Pro or Free
                                            }`}>

                                            {/* The Image */}
                                            {user?.avatar_url ? (
                                                <img
                                                    src={user.avatar_url}
                                                    alt="Profile"
                                                    className="w-full h-full rounded-full object-cover border-2 border-background"
                                                />
                                            ) : (
                                                <div className={`w-full h-full rounded-full flex items-center justify-center border-2 border-background ${userBgColor}`}>
                                                    <span className="font-heading text-xs md:text-sm text-white uppercase">
                                                        {(user?.username || user?.email || 'U')[0]}
                                                    </span>
                                                </div>
                                            )}

                                            {/* The Badge (Crown) - Scaled for Desktop */}
                                            {isPro && (
                                                <div className={`absolute -top-1.5 -right-1.5 rounded-full p-1 shadow-sm border border-background flex items-center justify-center 
                                                    w-5 h-5 md:w-6 md:h-6 transition-all duration-300
                                                    ${isAnnual
                                                        ? 'bg-[#FFD700] text-black'  // Annual: Gold Background, Black Icon
                                                        : 'bg-[#FFFFFF] text-primary-foreground' // Monthly: Brand Color, White/Black Icon
                                                    }`}>
                                                    <Crown className={`fill-current ${isAnnual ? "w-3 h-3 md:w-3.5 md:h-3.5" : "w-3 h-3 md:w-3.5 md:h-3.5"}`} />
                                                </div>
                                            )}
                                        </div>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-2 py-2 bg-muted/30">
                                        <p className="text-sm font-bold truncate">{user?.username || user?.email}</p>
                                        <p className="text-xs flex items-center gap-1.5 mt-1">
                                            {isAnnual ? (
                                                <>
                                                    <Crown className="w-3 h-3 text-[#FFD700] fill-[#FFD700]" />
                                                    <span className="text-[#FFD700] font-bold uppercase tracking-wide">Founding Member</span>
                                                </>
                                            ) : (
                                                <span className="text-muted-foreground">{isPro ? 'Pro Member' : 'Free Plan'}</span>
                                            )}
                                        </p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                                        <LayoutDashboard className="w-4 h-4 mr-2" />
                                        Dashboard
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                                        <UserCog className="w-4 h-4 mr-2" />
                                        Profile Settings
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                                            <Settings className="w-4 h-4 mr-2" />
                                            Admin Panel
                                        </DropdownMenuItem>
                                    )}
                                    {!isPro && (
                                        <DropdownMenuItem onClick={() => navigate('/pricing')} data-testid="menu-upgrade">
                                            <Crown className="w-4 h-4 mr-2" />
                                            Upgrade to Pro
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/login')}
                                    className="font-heading uppercase text-sm md:text-base"
                                    data-testid="nav-login"
                                >
                                    Login
                                </Button>
                                <Button
                                    onClick={() => navigate('/register')}
                                    className="btn-primary text-sm md:text-base"
                                    data-testid="nav-register"
                                >
                                    Get Started
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Mobile Controls (Avatar + Hamburger) */}
                    <div className="md:hidden flex items-center gap-3">
                        
                        {/* Mobile Avatar (Links directly to Profile) */}
                        {isAuthenticated && (
                            <button 
                                onClick={() => navigate('/profile')}
                                className="focus:outline-none"
                                title="Go to Profile"
                            >
                                <div className={`relative w-9 h-9 rounded-full flex items-center justify-center p-[2px] transition-all duration-300
                                    ${isAnnual
                                        ? 'bg-gradient-to-tr from-[#FFD700] via-[#FFA500] to-[#FFD700] shadow-[0_0_10px_rgba(255,215,0,0.5)]'
                                        : (isPro ? 'bg-primary/50' : 'bg-transparent')
                                    }`}>

                                    {/* The Image */}
                                    {user?.avatar_url ? (
                                        <img
                                            src={user.avatar_url}
                                            alt="Profile"
                                            className="w-full h-full rounded-full object-cover border-2 border-background"
                                        />
                                    ) : (
                                        <div className={`w-full h-full rounded-full flex items-center justify-center border-2 border-background ${userBgColor}`}>
                                            <span className="font-heading text-xs text-white uppercase">
                                                {(user?.username || user?.email || 'U')[0]}
                                            </span>
                                        </div>
                                    )}

                                    {/* The Badge (Crown) */}
                                    {isPro && (
                                        <div className={`absolute -top-1.5 -right-1.5 rounded-full p-1 shadow-sm border border-background flex items-center justify-center w-5 h-5 transition-all duration-300
                                            ${isAnnual
                                                ? 'bg-[#FFD700] text-black'
                                                : 'bg-[#FFFFFF] text-primary-foreground'
                                            }`}>
                                            <Crown className="fill-current w-3 h-3" />
                                        </div>
                                    )}
                                </div>
                            </button>
                        )}

                        {/* Hamburger Menu Toggle */}
                        <button
                            className="p-2"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? (
                                <X className="w-6 h-6" />
                            ) : (
                                <Menu className="w-6 h-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden glass border-t border-border animate-fade-in">
                    <div className="px-4 py-4 space-y-3">
                        {isAuthenticated ? (
                            // LOGGED-IN MENU
                            <>
                                <Link
                                    to="/dashboard"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/gallery"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Gallery
                                </Link>
                                <Link
                                    to="/pricing"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Pricing
                                </Link>
                                <Link
                                    to="/leaderboard"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Leaderboard
                                </Link>
                                <Link
                                    to="/faq"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    FAQ
                                </Link>
                                <Link
                                    to="/profile"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Profile
                                </Link>
                                {isAdmin && (
                                    <Link
                                        to="/admin"
                                        className="block font-heading text-sm uppercase tracking-wider py-2"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Admin
                                    </Link>
                                )}
                                <button
                                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                    className="block w-full text-left font-heading text-sm uppercase tracking-wider py-2 text-destructive"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            // GUEST MENU
                            <>
                                <Link
                                    to="/gallery"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Gallery
                                </Link>
                                <Link
                                    to="/pricing"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Pricing
                                </Link>
                                <Link
                                    to="/leaderboard"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Leaderboard
                                </Link>
                                <Link
                                    to="/faq"
                                    className="block font-heading text-sm uppercase tracking-wider py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    FAQ
                                </Link>
                                <div className="flex flex-col gap-2 pt-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                                        className="w-full"
                                    >
                                        Login
                                    </Button>
                                    <Button
                                        onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                                        className="btn-primary w-full"
                                    >
                                        Get Started
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export const Footer = () => {
    return (
        <footer className="border-t border-border bg-card/50 mt-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    {/* Brand */}
                    <div className="md:col-span-2">
                        <Link to="/" className="inline-block">
                            <img
                                src="/subhack_logo.svg"
                                alt="SubHack Logo"
                                className="h-20 w-auto object-contain mb-2"
                            />
                        </Link>

                        <p className="mt-4 text-muted-foreground text-sm max-w-md">
                            Create hilarious subtitled clips in seconds! Pick a video, add your captions,
                            and share your masterpiece with the world.
                        </p>
                    </div>

                    {/* Product Links */}
                    <div>
                        <h4 className="font-heading text-sm uppercase tracking-wider mb-4">Product</h4>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Dashboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/gallery" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Gallery
                                </Link>
                            </li>
                            <li>
                                <Link to="/leaderboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Leaderboard
                                </Link>
                            </li>
                            <li>
                                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Pricing
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Support Links */}
                    <div>
                        <h4 className="font-heading text-sm uppercase tracking-wider mb-4">Support</h4>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    FAQ
                                </Link>
                            </li>
                            <li>
                                {/* FIXED: Closed <a> tag properly instead of </Link> */}
                                <a href="mailto:support@subhack.app" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Contact Us
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Legal Links */}
                    <div>
                        <h4 className="font-heading text-sm uppercase tracking-wider mb-4">Legal</h4>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Terms of Service
                                </Link>
                            </li>
                            <li>
                                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Privacy Policy
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                        © {new Date().getFullYear()} All the Rage Ventures, LLC. All rights reserved.
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                        No clips were harmed during the building of this app. 💛
                    </p>
                </div>
            </div>
        </footer>
    );
};

export const Layout = ({ children, showFooter = true }) => {
    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            {/* PADDING CHANGE:
                Mobile: pt-16 (to clear 64px header)
                Desktop: md:pt-24 (to clear 96px header)
            */}
            <main className="flex-1 pt-16 md:pt-24 transition-all duration-300">
                {children}
            </main>
            {showFooter && <Footer />}
        </div>
    );
};

export default Layout;